import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import Info from "components/icons/Info";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { BarOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { OnboardingImage, SoftwarePackage } from "domain/softwarePackages";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    fetchedSoftwarePackages?: SoftwarePackage[];
    fetchedOnboardingImages?: OnboardingImage[];
}

interface Step {
    description: string | JSX.Element;
    action?: JSX.Element;
}

const SetupWinEraser = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BarOnboardingSteps.STEP_SETUP_WINERASER;

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: OnboardingProductType.BAR,
            step: BarOnboardingSteps.STEP_SETUP_WINERASER,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const preparationSteps: Step[] = [
        {
            description: t("Onboarding.barHomePage.setupWinEraser.preparation.ubuntuMachine"),
        },
        {
            description: t("Onboarding.barHomePage.setupWinEraser.preparation.windowsMachine"),
        },
        {
            description: t("Onboarding.barHomePage.setupWinEraser.preparation.referenceMachine"),
        },
        {
            description: t("Onboarding.barHomePage.setupWinEraser.preparation.networkCable"),
        },
        {
            description: t("Onboarding.barHomePage.setupWinEraser.preparation.usbSticks"),
        },
    ];

    function createContent() {
        return (
            <>
                <div className={style.stepsContainer}>
                    <div className={style.bannerText}>{t("Onboarding.barHomePage.setupWinEraser.description")}</div>
                    {preparationSteps.map((step, index) => (
                        <div key={index}>
                            <div className={style.subStepsToFollowLayout}>
                                <CircleNumber number={index + 1} subStep={true} />
                                <div className={style.stepPadding}>
                                    <div>{step.description}</div>
                                </div>
                            </div>
                            {index === 0 && (
                                <div className={style.alertBox}>
                                    <div>
                                        <Info
                                            borderColor={theme.contentBackgroundColor}
                                            color={theme.sortIconActiveColor}
                                            size={"18"}
                                        />
                                    </div>
                                    <div className={style.bannerText}>
                                        {t("Onboarding.barHomePage.setupWinEraser.ubuntuRequirements.description")}
                                        <ul className={style.listPadding}>
                                            <li>{t("Onboarding.barHomePage.setupWinEraser.ubuntuRequirements.cpu")}</li>
                                            <li>
                                                {t("Onboarding.barHomePage.setupWinEraser.ubuntuRequirements.storage")}
                                            </li>
                                            <li>{t("Onboarding.barHomePage.setupWinEraser.ubuntuRequirements.ram")}</li>
                                            <li>
                                                {t("Onboarding.barHomePage.setupWinEraser.ubuntuRequirements.network")}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            {index === 2 && (
                                <div className={style.alertBox}>
                                    <div>
                                        <Info
                                            borderColor={theme.contentBackgroundColor}
                                            color={theme.sortIconActiveColor}
                                            size={"18"}
                                        />
                                    </div>
                                    <div className={style.bannerText}>
                                        {t("Onboarding.barHomePage.setupWinEraser.referenceRequirements.description")}
                                        <ul className={style.listPadding}>
                                            <li>
                                                {t(
                                                    "Onboarding.barHomePage.setupWinEraser.referenceRequirements.ethernet"
                                                )}
                                            </li>
                                            <li>
                                                {t("Onboarding.barHomePage.setupWinEraser.referenceRequirements.boot")}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <div className={style.templateSelectedBlock}>
                            <StepCompletedText
                                successText={t("Onboarding.gettingStartedFlow.completed")}
                                testId={testIds.workArea.onboarding.step.statusLabel}
                                dataType={OnboardingProductType.BAR + "_" + stepName}
                            />
                        </div>
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                saveOnboardingStep();
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BAR + "_" + stepName}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.barHomePage.setupWinEraser.markComplete")}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.barHomePage.setupWinEraser.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BAR}
            stepName={stepName}
        />
    );
};

export default connector(SetupWinEraser);
